'use client';
import React, { useState } from 'react';
import { X, MapPin, Clock, User, Phone, FileText, Camera, Users, Plus, Trash2, CheckCircle, PoundSterling, Calculator, Clock3, XCircle, AlertCircle, Star, ShieldAlert, Edit2, Send, RefreshCw, Zap } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { createClient } from '@/lib/supabase/client';

type KanbanStatus = 'pending' | 'assigned' | 'inprogress' | 'completed' | 'cancelled';

export interface JobDetail {
  id: string;
  service: string;
  address: string;
  customer: string;
  customerPhone: string;
  customerEmail: string;
  date: string;
  time: string;
  status: KanbanStatus;
  cleaners: string[];
  price: number;
  payout: number;
  bedrooms: number;
  bathrooms: number;
  description: string;
  customerPhotos: { url: string; alt: string; roomLabel?: string }[];
  customerAdditionalPhotos?: { url: string; alt: string }[];
  cleanerPhotos: { url: string; alt: string; uploadedBy: string }[];
  customerApproved?: boolean;
  cleanlinessRating?: number;
}

interface JobDetailModalProps {
  job: JobDetail | null;
  onClose: () => void;
  onUpdateJob: (updatedJob: JobDetail) => void;
  availableCleaners: { id: string; name: string; payoutRate?: number }[];
}

const statusVariantMap: Record<KanbanStatus, 'pending' | 'assigned' | 'inprogress' | 'completed' | 'cancelled'> = {
  pending: 'pending',
  assigned: 'assigned',
  inprogress: 'inprogress',
  completed: 'completed',
  cancelled: 'cancelled',
};

export default function JobDetailModal({ job, onClose, onUpdateJob, availableCleaners }: JobDetailModalProps) {
  const [selectedCleaner, setSelectedCleaner] = useState('');
  const [activeTab, setActiveTab] = useState<'details' | 'photos' | 'cleaners' | 'payout'>('details');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<{ approved: boolean; rating: number; notes: string } | null>(null);
  const [loadingApproval, setLoadingApproval] = useState(false);

  // Payout state
  const [payMode, setPayMode] = useState<'custom' | 'hourly'>('custom');
  const [selectedPayoutCleaner, setSelectedPayoutCleaner] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [hoursWorked, setHoursWorked] = useState('');
  const [minutesWorked, setMinutesWorked] = useState('');
  const [customHourlyRate, setCustomHourlyRate] = useState('');
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState(false);
  const [payoutError, setPayoutError] = useState('');

  // Stripe disbursement state
  const [disbursingPayoutId, setDisbursingPayoutId] = useState<string | null>(null);
  const [disbursementSuccess, setDisbursementSuccess] = useState<string | null>(null);
  const [disbursementError, setDisbursementError] = useState('');
  const [recentPayouts, setRecentPayouts] = useState<Array<{id: string; cleaner_name: string; payout_amount: string; status: string; stripe_transfer_id?: string; disbursement_method?: string}>>([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);

  // Price adjustment state
  const [editingPrice, setEditingPrice] = useState(false);
  const [adjustedPrice, setAdjustedPrice] = useState('');
  const [priceNote, setPriceNote] = useState('');
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceSent, setPriceSent] = useState(false);
  const [priceError, setPriceError] = useState('');

  if (!job) return null;

  // Load customer approval status when payout tab is opened
  const handleTabChange = async (tab: typeof activeTab) => {
    setActiveTab(tab);
    setPayoutSuccess(false);
    setPayoutError('');
    setDisbursementSuccess(null);
    setDisbursementError('');
    if (tab === 'payout' && !approvalStatus) {
      setLoadingApproval(true);
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('job_approvals')
          .select('approved, cleanliness_rating, approval_notes')
          .eq('job_id', job.id)
          .maybeSingle();
        if (data) {
          setApprovalStatus({ approved: data.approved, rating: data.cleanliness_rating, notes: data.approval_notes || '' });
        }
      } catch {
        // silently fail
      } finally {
        setLoadingApproval(false);
      }
    }
    if (tab === 'payout') {
      setLoadingPayouts(true);
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('cleaner_payouts')
          .select('id, cleaner_name, payout_amount, status, stripe_transfer_id, disbursement_method')
          .eq('job_id', job.id)
          .order('created_at', { ascending: false });
        if (data) setRecentPayouts(data);
      } catch {
        // silently fail
      } finally {
        setLoadingPayouts(false);
      }
    }
  };

  const unassignedCleaners = availableCleaners.filter(c => !job.cleaners.includes(c.name));

  const handleAddCleaner = () => {
    if (!selectedCleaner) return;
    const cleaner = availableCleaners.find(c => c.id === selectedCleaner);
    if (!cleaner) return;
    // Check if cleaner is in verifiedCleanerIds — in production this would come from DB
    // For now we allow assignment but show a warning banner
    const updatedCleaners = [...job.cleaners, cleaner.name];
    const newStatus: KanbanStatus = job.status === 'pending' ? 'assigned' : job.status;
    onUpdateJob({ ...job, cleaners: updatedCleaners, status: newStatus });
    setSelectedCleaner('');
  };

  const handleRemoveCleaner = (cleanerName: string) => {
    const updatedCleaners = job.cleaners.filter(c => c !== cleanerName);
    const newStatus: KanbanStatus = updatedCleaners.length === 0 ? 'pending' : job.status;
    onUpdateJob({ ...job, cleaners: updatedCleaners, status: newStatus });
  };

  // Payout calculations
  const getCleanerHourlyRate = (cleanerName: string) => {
    const cleaner = availableCleaners.find(c => c.name === cleanerName);
    return cleaner?.payoutRate ?? null;
  };

  const computedHourlyPayout = (() => {
    if (!selectedPayoutCleaner || payMode !== 'hourly') return 0;
    const h = parseInt(hoursWorked || '0', 10);
    const m = parseInt(minutesWorked || '0', 10);
    const cleanerRate = getCleanerHourlyRate(selectedPayoutCleaner);
    const rate = customHourlyRate !== '' ? parseFloat(customHourlyRate) : (cleanerRate ?? 0);
    if (isNaN(rate) || rate <= 0) return 0;
    return parseFloat(((h + m / 60) * rate).toFixed(2));
  })();

  const payoutAmount = payMode === 'custom' ? parseFloat(customAmount || '0') : computedHourlyPayout;
  const deduction = parseFloat((job.price - payoutAmount).toFixed(2));

  const handleConfirmPayout = async () => {
    if (!selectedPayoutCleaner) { setPayoutError('Please select a cleaner.'); return; }
    if (payoutAmount <= 0) { setPayoutError('Payout amount must be greater than £0.'); return; }
    if (payoutAmount > job.price) { setPayoutError('Payout cannot exceed the customer payment.'); return; }

    setPayoutError('');
    setPayoutSaving(true);
    try {
      const supabase = createClient();
      const h = parseInt(hoursWorked || '0', 10);
      const m = parseInt(minutesWorked || '0', 10);
      const { error } = await supabase.from('cleaner_payouts').insert({
        job_id: job.id,
        cleaner_name: selectedPayoutCleaner,
        job_service: job.service,
        job_address: job.address,
        customer_paid: job.price,
        payout_amount: payoutAmount,
        pay_mode: payMode,
        hours_worked: payMode === 'hourly' ? h : 0,
        minutes_worked: payMode === 'hourly' ? m : 0,
        hourly_rate: payMode === 'hourly' ? (customHourlyRate !== '' ? parseFloat(customHourlyRate) : (getCleanerHourlyRate(selectedPayoutCleaner) ?? 0)) : 0,
        status: 'pending',
      });
      if (error) throw error;
      setPayoutSuccess(true);
      // Reset form
      setCustomAmount('');
      setHoursWorked('');
      setMinutesWorked('');
    } catch (err: unknown) {
      setPayoutError(err instanceof Error ? err.message : 'Failed to save payout. Please try again.');
    } finally {
      setPayoutSaving(false);
    }
  };

  const handleDisbursePayout = async (payoutId: string) => {
    setDisbursingPayoutId(payoutId);
    setDisbursementError('');
    setDisbursementSuccess(null);
    try {
      const supabase = createClient();
      const { data, error } = await supabase.functions.invoke<{
        success: boolean;
        disbursementMethod: string;
        stripeTransferId?: string;
      }>('disburse-cleaner-payout', {
        body: { payoutId },
      });
      if (error) {
        const msg = (data as { error?: string })?.error ?? error.message ?? 'Disbursement failed';
        setDisbursementError(msg);
        return;
      }
      if (data?.success) {
        const method = data.disbursementMethod === 'stripe_connect' ? 'via Stripe Connect' : 'manually';
        setDisbursementSuccess(`Payout disbursed ${method}!`);
        // Refresh payout list
        const { data: updated } = await supabase
          .from('cleaner_payouts')
          .select('id, cleaner_name, payout_amount, status, stripe_transfer_id, disbursement_method')
          .eq('job_id', job.id)
          .order('created_at', { ascending: false });
        if (updated) setRecentPayouts(updated);
      }
    } catch (err: unknown) {
      setDisbursementError(err instanceof Error ? err.message : 'Disbursement failed');
    } finally {
      setDisbursingPayoutId(null);
    }
  };

  const handleSendAdjustedPrice = async () => {
    const newPrice = parseFloat(adjustedPrice);
    if (isNaN(newPrice) || newPrice <= 0) {
      setPriceError('Please enter a valid price greater than £0.');
      return;
    }
    if (newPrice === job.price) {
      setPriceError('The new price is the same as the current price.');
      return;
    }
    setPriceError('');
    setPriceSaving(true);
    try {
      const supabase = createClient();
      // Record the price adjustment
      const { error: updateError } = await supabase
        .from('jobs')
        .update({ price: newPrice, price_adjustment_note: priceNote || null, price_adjusted_at: new Date().toISOString() })
        .eq('id', job.id);

      if (updateError) throw updateError;

      // Insert a notification for the customer
      await supabase.from('notifications').insert({
        recipient_role: 'customer',
        recipient_id: job.id, // In production this would be the actual customer user ID
        type: 'job',
        title: 'Updated Balance to Pay',
        message: `Your job "${job.service}" has an updated price of £${newPrice.toFixed(2)}. ${priceNote ? `Note: ${priceNote}` : 'Please log in to complete your payment.'}`,
        is_read: false,
        job_id: job.id,
        metadata: {
          previous_price: job.price,
          new_price: newPrice,
          adjustment_note: priceNote,
          job_service: job.service,
        },
      });

      onUpdateJob({ ...job, price: newPrice });
      setPriceSent(true);
      setEditingPrice(false);
      setAdjustedPrice('');
      setPriceNote('');
    } catch (err: unknown) {
      setPriceError(err instanceof Error ? err.message : 'Failed to update price. Please try again.');
    } finally {
      setPriceSaving(false);
    }
  };

  const handleCancelJob = () => {
    onUpdateJob({ ...job, status: 'cancelled' });
    setShowCancelConfirm(false);
  };

  const tabs = [
    { id: 'details' as const, label: 'Job Details', icon: FileText },
    { id: 'photos' as const, label: 'Photos', icon: Camera },
    { id: 'cleaners' as const, label: 'Cleaners', icon: Users },
    { id: 'payout' as const, label: 'Payout', icon: PoundSterling },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-card rounded-2xl card-shadow-lg z-10 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-foreground">{job.service}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant={statusVariantMap[job.status]} label={job.status === 'inprogress' ? 'In Progress' : job.status.charAt(0).toUpperCase() + job.status.slice(1)} size="sm" />
                <span className="text-xs text-muted-foreground">#{job.id}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6 flex-shrink-0 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-semibold border-b-2 transition-colors mr-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-5">

          {/* DETAILS TAB */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              {/* Customer Info */}
              <div>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Customer Information</h3>
                <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User size={14} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{job.customer}</p>
                      <p className="text-xs text-muted-foreground">Customer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Phone size={14} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{job.customerPhone}</p>
                      <p className="text-xs text-muted-foreground">Phone</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{job.address}</p>
                      <p className="text-xs text-muted-foreground">Address</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <Clock size={14} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{job.date} · {job.time}</p>
                      <p className="text-xs text-muted-foreground">Scheduled</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Description */}
              <div>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Job Description</h3>
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-sm text-foreground leading-relaxed">{job.description}</p>
                </div>
              </div>

              {/* Property Details */}
              <div>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Property Details</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-foreground">{job.bedrooms}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Bedrooms</p>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-foreground">{job.bathrooms}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Bathrooms</p>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-3 text-center">
                    <p className="text-xl font-bold text-success">£{job.payout}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Payout</p>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-xl p-4">
                <div>
                  <p className="text-xs text-muted-foreground">Customer Price</p>
                  <p className="text-lg font-bold text-foreground">£{job.price}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Cleaner Payout</p>
                  <p className="text-lg font-bold text-success">£{job.payout}</p>
                </div>
              </div>

              {/* Admin Price Adjustment */}
              {job.status !== 'cancelled' && (
                <div>
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Adjust Customer Price</h3>
                  {priceSent && !editingPrice ? (
                    <div className="bg-success-bg border border-success/20 rounded-xl p-4 flex items-start gap-3">
                      <CheckCircle size={16} className="text-success flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-success">Updated price sent to customer</p>
                        <p className="text-xs text-muted-foreground mt-0.5">The customer has been notified to pay the updated balance of £{job.price.toFixed(2)}.</p>
                      </div>
                      <button
                        onClick={() => { setPriceSent(false); setEditingPrice(true); setAdjustedPrice(String(job.price)); }}
                        className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted transition-colors flex-shrink-0"
                        title="Adjust again"
                      >
                        <RefreshCw size={13} />
                      </button>
                    </div>
                  ) : editingPrice ? (
                    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">New Customer Price (£)</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold text-sm">£</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={adjustedPrice}
                            onChange={e => setAdjustedPrice(e.target.value)}
                            placeholder={job.price.toFixed(2)}
                            className="w-full pl-8 pr-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">Note to Customer (optional)</label>
                        <input
                          type="text"
                          value={priceNote}
                          onChange={e => setPriceNote(e.target.value)}
                          placeholder="e.g. Additional rooms cleaned, extra supplies used..."
                          className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                        />
                      </div>
                      {adjustedPrice && parseFloat(adjustedPrice) > 0 && parseFloat(adjustedPrice) !== job.price && (
                        <div className="bg-muted/50 rounded-xl p-3 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Price change</span>
                          <span className={`font-bold ${parseFloat(adjustedPrice) > job.price ? 'text-success' : 'text-danger'}`}>
                            £{job.price.toFixed(2)} → £{parseFloat(adjustedPrice).toFixed(2)}
                            {' '}({parseFloat(adjustedPrice) > job.price ? '+' : ''}£{(parseFloat(adjustedPrice) - job.price).toFixed(2)})
                          </span>
                        </div>
                      )}
                      {priceError && (
                        <p className="text-xs text-danger bg-danger/10 rounded-xl px-4 py-2">{priceError}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={handleSendAdjustedPrice}
                          disabled={priceSaving || !adjustedPrice || parseFloat(adjustedPrice) <= 0}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-50"
                        >
                          {priceSaving ? (
                            <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending…</span>
                          ) : (
                            <><Send size={13} /> Send to Customer</>
                          )}
                        </button>
                        <button
                          onClick={() => { setEditingPrice(false); setAdjustedPrice(''); setPriceNote(''); setPriceError(''); }}
                          className="px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingPrice(true); setAdjustedPrice(String(job.price)); }}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-primary/30 text-primary text-sm font-semibold hover:bg-primary/5 transition-colors"
                    >
                      <Edit2 size={14} />
                      Adjust Price &amp; Send to Customer
                    </button>
                  )}
                </div>
              )}

              {/* Cancel Job */}
              {job.status !== 'completed' && job.status !== 'cancelled' && (
                <div>
                  {showCancelConfirm ? (
                    <div className="bg-danger/5 border border-danger/20 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <XCircle size={16} className="text-danger flex-shrink-0" />
                        <p className="text-sm font-semibold text-danger">Cancel this job?</p>
                      </div>
                      <p className="text-xs text-muted-foreground">This will mark the job as Cancelled. This action cannot be undone.</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancelJob}
                          className="flex-1 py-2 rounded-xl bg-danger text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all"
                        >
                          Yes, Cancel Job
                        </button>
                        <button
                          onClick={() => setShowCancelConfirm(false)}
                          className="flex-1 py-2 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                        >
                          Keep Job
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-danger/30 text-danger text-sm font-semibold hover:bg-danger/5 transition-colors"
                    >
                      <XCircle size={14} />
                      Cancel Job
                    </button>
                  )}
                </div>
              )}

              {job.status === 'cancelled' && (
                <div className="bg-muted/50 border border-border rounded-xl p-4 flex items-center gap-3">
                  <XCircle size={18} className="text-muted-foreground flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground">Job Cancelled</p>
                    <p className="text-xs text-muted-foreground">This job has been cancelled by the admin.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PHOTOS TAB */}
          {activeTab === 'photos' && (
            <div className="space-y-6">
              {/* Customer Room Photos */}
              <div>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                  Customer Room Photos ({job.customerPhotos.length})
                </h3>
                {job.customerPhotos.length > 0 ? (
                  (() => {
                    // Group by roomLabel if present
                    const grouped: Record<string, typeof job.customerPhotos> = {};
                    job.customerPhotos.forEach(photo => {
                      const key = photo.roomLabel || 'General';
                      if (!grouped[key]) grouped[key] = [];
                      grouped[key].push(photo);
                    });
                    const groups = Object.entries(grouped);
                    return (
                      <div className="space-y-4">
                        {groups.map(([roomLabel, photos]) => (
                          <div key={roomLabel}>
                            <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                              <Camera size={11} />
                              {roomLabel}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {photos.map((photo, i) => (
                                <div key={i} className="aspect-square rounded-xl overflow-hidden bg-muted border border-border">
                                  <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                ) : (
                  <div className="bg-muted/50 rounded-xl p-8 text-center">
                    <Camera size={24} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No customer room photos uploaded</p>
                  </div>
                )}
              </div>

              {/* Customer Additional Photos */}
              {(job.customerAdditionalPhotos?.length ?? 0) > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                    Additional Photos — Items / Walls / Floors / Appliances ({job.customerAdditionalPhotos!.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {job.customerAdditionalPhotos!.map((photo, i) => (
                      <div key={i} className="aspect-square rounded-xl overflow-hidden bg-muted border border-border">
                        <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cleaner Photos */}
              <div>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                  Cleaner Uploaded Photos ({job.cleanerPhotos.length})
                </h3>
                {job.cleanerPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {job.cleanerPhotos.map((photo, i) => (
                      <div key={i} className="rounded-xl overflow-hidden bg-muted border border-border">
                        <div className="aspect-square">
                          <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
                        </div>
                        <div className="px-2 py-1.5 bg-card border-t border-border">
                          <p className="text-xs text-muted-foreground truncate">By {photo.uploadedBy}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-muted/50 rounded-xl p-8 text-center">
                    <Camera size={24} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No cleaner photos uploaded yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CLEANERS TAB */}
          {activeTab === 'cleaners' && (
            <div className="space-y-5">
              {/* Assigned Cleaners */}
              <div>
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                  Assigned Cleaners ({job.cleaners.length})
                </h3>
                {job.cleaners.length > 0 ? (
                  <div className="space-y-2">
                    {job.cleaners.map((cleanerName, i) => {
                      const rate = getCleanerHourlyRate(cleanerName);
                      return (
                        <div key={i} className="flex items-center justify-between bg-muted/50 rounded-xl p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <User size={14} className="text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{cleanerName}</p>
                              {rate !== null && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <CheckCircle size={10} className="text-success" />
                                  <p className="text-xs text-success">Assigned</p>
                                </div>
                              )}
                            </div>
                          </div>
                          {job.status !== 'completed' && (
                            <button
                              onClick={() => handleRemoveCleaner(cleanerName)}
                              className="p-1.5 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                              title="Remove cleaner"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-muted/50 rounded-xl p-6 text-center">
                    <Users size={24} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No cleaners assigned yet</p>
                  </div>
                )}
              </div>

              {/* Add Cleaner */}
              {job.status !== 'completed' && unassignedCleaners.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Add Cleaner</h3>
                  <div className="bg-warning-bg/30 border border-warning/30 rounded-xl p-3 mb-3 flex items-start gap-2">
                    <ShieldAlert size={14} className="text-warning flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-foreground">Only assign cleaners who have been <span className="font-semibold text-warning">verified</span> via the Onboarding tab. Unverified cleaners cannot legally perform jobs.</p>
                  </div>
                  <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                    <select
                      value={selectedCleaner}
                      onChange={e => setSelectedCleaner(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                    >
                      <option value="">Choose a cleaner to add...</option>
                      {unassignedCleaners.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={handleAddCleaner}
                      disabled={!selectedCleaner}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-50"
                    >
                      <Plus size={14} />
                      Add Cleaner
                    </button>
                  </div>
                </div>
              )}

              {job.status !== 'completed' && unassignedCleaners.length === 0 && job.cleaners.length > 0 && (
                <div className="bg-success-bg/50 border border-success/20 rounded-xl p-4 text-center">
                  <CheckCircle size={20} className="text-success mx-auto mb-1.5" />
                  <p className="text-sm font-semibold text-success">All available cleaners assigned</p>
                </div>
              )}
            </div>
          )}

          {/* PAYOUT TAB */}
          {activeTab === 'payout' && (
            <div className="space-y-5">
              {/* Customer Approval Status */}
              {job.status === 'completed' && (
                <div>
                  {loadingApproval ? (
                    <div className="bg-muted/50 rounded-xl p-4 flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      <p className="text-sm text-muted-foreground">Checking customer approval...</p>
                    </div>
                  ) : approvalStatus?.approved ? (
                    <div className="bg-success-bg border border-success/20 rounded-xl p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-success flex-shrink-0" />
                        <p className="text-sm font-semibold text-success">Customer Has Approved This Job</p>
                      </div>
                      {approvalStatus.rating > 0 && (
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs text-muted-foreground">Cleanliness Rating:</p>
                          <div className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map(s => (
                              <Star key={s} size={12} className={s <= approvalStatus.rating ? 'fill-warning text-warning' : 'text-muted-foreground'} />
                            ))}
                          </div>
                          <span className="text-xs font-semibold text-foreground">{approvalStatus.rating}/5</span>
                        </div>
                      )}
                      {approvalStatus.notes && (
                        <p className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2">&ldquo;{approvalStatus.notes}&rdquo;</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-warning-bg border border-warning/20 rounded-xl p-4 flex items-start gap-3">
                      <AlertCircle size={16} className="text-warning flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-warning">Awaiting Customer Approval</p>
                        <p className="text-xs text-muted-foreground mt-0.5">The customer has not yet approved this job. Payout can still be recorded but it is recommended to wait for customer approval.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Customer Payment Summary */}
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Job Payment Summary</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Customer Paid</p>
                    <p className="text-2xl font-extrabold text-foreground">£{job.price.toFixed(2)}</p>
                  </div>
                  <div className="w-px h-10 bg-border" />
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Your Margin</p>
                    <p className={`text-2xl font-extrabold ${deduction >= 0 ? 'text-success' : 'text-danger'}`}>
                      £{deduction.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {payoutSuccess ? (
                <div className="bg-success-bg border border-success/20 rounded-xl p-6 text-center">
                  <CheckCircle size={32} className="text-success mx-auto mb-2" />
                  <p className="text-sm font-bold text-success">Payout Confirmed!</p>
                  <p className="text-xs text-muted-foreground mt-1">£{payoutAmount.toFixed(2)} payout has been recorded for {selectedPayoutCleaner}.</p>
                  <button
                    onClick={() => { setPayoutSuccess(false); setSelectedPayoutCleaner(''); }}
                    className="mt-4 px-4 py-2 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                  >
                    Pay Another Cleaner
                  </button>
                </div>
              ) : (
                <>
                  {/* Select Cleaner */}
                  <div>
                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Select Cleaner to Pay</h3>
                    {job.cleaners.length > 0 ? (
                      <div className="space-y-2">
                        {job.cleaners.map((cleanerName, i) => (
                          <button
                            key={i}
                            onClick={() => setSelectedPayoutCleaner(cleanerName)}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                              selectedPayoutCleaner === cleanerName
                                ? 'border-primary bg-primary/5' :'border-border bg-muted/30 hover:bg-muted/60'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <User size={14} className="text-primary" />
                            </div>
                            <div className="flex-1 text-left">
                              <p className="text-sm font-semibold text-foreground">{cleanerName}</p>
                            </div>
                            {selectedPayoutCleaner === cleanerName && (
                              <CheckCircle size={16} className="text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-muted/50 rounded-xl p-4 text-center">
                        <p className="text-sm text-muted-foreground">No cleaners assigned to this job yet.</p>
                      </div>
                    )}
                  </div>

                  {selectedPayoutCleaner && (
                    <>
                      {/* Pay Mode Toggle */}
                      <div>
                        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Pay Mode</h3>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => setPayMode('custom')}
                            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-semibold transition-all ${
                              payMode === 'custom' ?'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:bg-muted'
                            }`}
                          >
                            <PoundSterling size={14} />
                            Custom Amount
                          </button>
                          <button
                            onClick={() => setPayMode('hourly')}
                            className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-semibold transition-all ${
                              payMode === 'hourly' ?'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:bg-muted'
                            }`}
                          >
                            <Clock3 size={14} />
                            Hourly Rate
                          </button>
                        </div>
                      </div>

                      {/* Custom Amount Input */}
                      {payMode === 'custom' && (
                        <div>
                          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Enter Payout Amount</h3>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold text-sm">£</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={customAmount}
                              onChange={e => setCustomAmount(e.target.value)}
                              placeholder="0.00"
                              className="w-full pl-8 pr-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                            />
                          </div>
                        </div>
                      )}

                      {/* Hourly Input */}
                      {payMode === 'hourly' && (
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Hourly Rate (£/hr)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.50"
                              value={customHourlyRate}
                              onChange={e => setCustomHourlyRate(e.target.value)}
                              placeholder={getCleanerHourlyRate(selectedPayoutCleaner) !== null ? `${getCleanerHourlyRate(selectedPayoutCleaner)} (default)` : 'Enter rate'}
                              className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                            />
                          </div>
                          <div>
                            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
                              Time Worked · £{customHourlyRate !== '' ? parseFloat(customHourlyRate) || 0 : (getCleanerHourlyRate(selectedPayoutCleaner) ?? 0)}/hr
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Hours</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="24"
                                  value={hoursWorked}
                                  onChange={e => setHoursWorked(e.target.value)}
                                  placeholder="0"
                                  className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-muted-foreground mb-1 block">Minutes</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="59"
                                  value={minutesWorked}
                                  onChange={e => setMinutesWorked(e.target.value)}
                                  placeholder="0"
                                  className="w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Payout Breakdown */}
                      {payoutAmount > 0 && (
                        <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Payout Breakdown</p>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Customer Paid</span>
                            <span className="font-semibold text-foreground">£{job.price.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Cleaner Payout</span>
                            <span className="font-semibold text-success">£{payoutAmount.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-border pt-2 flex justify-between text-sm">
                            <span className="font-semibold text-foreground">Your Profit</span>
                            <span className={`font-bold ${deduction >= 0 ? 'text-success' : 'text-danger'}`}>
                              £{deduction.toFixed(2)}
                            </span>
                          </div>
                          {payMode === 'hourly' && (
                            <p className="text-xs text-muted-foreground pt-1">
                              <Calculator size={10} className="inline mr-1" />
                                            {hoursWorked || '0'}h {minutesWorked || '0'}m × £{customHourlyRate !== '' ? parseFloat(customHourlyRate) || 0 : (getCleanerHourlyRate(selectedPayoutCleaner) ?? 0)}/hr
                            </p>
                          )}
                        </div>
                      )}

                      {/* Error */}
                      {payoutError && (
                        <p className="text-xs text-danger bg-danger/10 rounded-xl px-4 py-2">{payoutError}</p>
                      )}

                      {/* Confirm Button */}
                      <button
                        onClick={handleConfirmPayout}
                        disabled={payoutSaving || payoutAmount <= 0}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-50"
                      >
                        {payoutSaving ? (
                          <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</span>
                        ) : (
                          <><PoundSterling size={14} /> Confirm Payout · £{payoutAmount.toFixed(2)}</>
                        )}
                      </button>
                    </>
                  )}
                </>
              )}

              {/* Recorded Payouts — Stripe Disbursement */}
              {(recentPayouts.length > 0 || loadingPayouts) && (
                <div>
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Recorded Payouts — Disburse via Stripe</h3>
                  {loadingPayouts ? (
                    <div className="bg-muted/50 rounded-xl p-4 flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                      <p className="text-xs text-muted-foreground">Loading payouts…</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {disbursementSuccess && (
                        <div className="bg-success-bg border border-success/20 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-success font-semibold">
                          <CheckCircle size={14} />
                          {disbursementSuccess}
                        </div>
                      )}
                      {disbursementError && (
                        <div className="bg-danger/10 border border-danger/20 rounded-xl px-4 py-3 text-sm text-danger">
                          {disbursementError}
                        </div>
                      )}
                      {recentPayouts.map((p) => (
                        <div key={p.id} className="bg-muted/50 rounded-xl p-3 flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">{p.cleaner_name}</p>
                            <p className="text-xs text-muted-foreground">£{parseFloat(p.payout_amount).toFixed(2)}</p>
                          </div>
                          {p.status === 'paid' ? (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <CheckCircle size={13} className="text-success" />
                              <span className="text-xs font-semibold text-success">
                                {p.disbursement_method === 'stripe_connect' ? 'Stripe Connect' : 'Paid'}
                              </span>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleDisbursePayout(p.id)}
                              disabled={disbursingPayoutId === p.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl gradient-primary text-white text-xs font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-60 flex-shrink-0"
                            >
                              {disbursingPayoutId === p.id ? (
                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                <Zap size={11} />
                              )}
                              Disburse
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
